import React, { useState, useCallback, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  useUserAssignments,
  useBulkUserAssignmentSave,
  EXCLUDE,
  decideExcludeOrCancel,
  usePrintAssignments,
  useEmailAssignments,
  useDeleteAndExclude,
  useUserAssignment,
  useUpdateExclusion,
  CANCEL
} from "../../hooks/userAssignments";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import {
  FiberManualRecord as DotIcon,
  Add,
  Print,
  Email
} from "@mui/icons-material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import AssignmentDetail from "./AssignmentDetail";
import CourseProgressModal from "./CourseProgressModal";
import ExclusionDetail from "./ExclusionDetail";
import Table from "../../components/ReactTable/Table";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import Modal from "../../components/Modal/Modal";
import { isEqual } from "lodash";
import { assignmentStatuses } from "../../constants";
import CourseExclusionSettingsModal from "../Content/Courses/CourseDetails/CourseExclusionSettingsModal";
import usePermissions from "../../redux/selectors/usePermissions";
import CreateAssignmentsModal from "./CreateAssignmentsModal";
import TooltipButton from "../../components/Buttons/TooltipButton";
import useModal from "../../hooks/useModal";

const useStyles = makeStyles(theme => ({
  dotIcon: {
    fontSize: "0.75rem",
    marginRight: theme.spacing(0.75)
  },
  dueDate: {
    textTransform: "capitalize"
  },
  addAssignmentButton: {
    textTransform: "none"
  }
}));

const getDotColor = (dueByColor, theme) => {
  switch (dueByColor) {
    case "red":
      return theme.palette.error.main;
    case "yellow":
      return "yellow";
    case "grey":
      return theme.palette.secondary[500];
    case "orange":
      return "orange";
    default:
      return theme.palette.primary.light;
  }
};

const decideDateLabel = (dueDate, exclusion) => {
  const daysUntilDue = moment(dueDate)
    .endOf("day")
    .diff(moment().endOf("day"), "days");
  let label = { label: `${daysUntilDue} Days`, color: "yellow" };
  if (exclusion) {
    label = { label: "Excluded", color: "grey" };
  } else if (daysUntilDue === 0) {
    label = { label: "Today", color: "red" };
  } else if (daysUntilDue < 0) {
    label = { label: `${Math.abs(daysUntilDue)} Days Overdue`, color: "red" };
  } else if (daysUntilDue > 30) {
    label = { label: `${moment(dueDate).format("MM/DD/YYYY")}`, color: "grey" };
  } else if (daysUntilDue <= 7) {
    label = { label: `${daysUntilDue} Days`, color: "orange" };
  }
  return label;
};

const DueDateDot = ({ row, dueDate }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { label, color } = decideDateLabel(dueDate, !!row.exclusion);
  const dotColor = getDotColor(color, theme);
  return (
    <Box display="flex" alignItems="center">
      <DotIcon className={classes.dotIcon} htmlColor={dotColor} />
      <Typography
        variant="body2"
        noWrap
        className={classes.dueDate}
        sx={{ pl: 0.5 }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const formatBatchUpdate = data =>
  data.map(datum => ({
    id: datum.id,
    attributes: { dueDate: datum.dueDate },
    assignmentType: { name: datum.assignmentType.name }
  }));

const Comments = ({ row, t }) => {
  return (
    <>
      <Typography variant="body2" noWrap>
        {row.assignmentType.description}
      </Typography>
      <Typography variant="body2" noWrap>
        {`${t("employees.ilt.table.columns.comments.assignedBy")}: ${
          row.assignedBy
            ? row.assignedBy.fullName
            : t("employees.ilt.table.columns.comments.assignedByNull")
        }`}
      </Typography>
      <Typography variant="body2">
        {decideExcludeOrCancel(row.assignmentType.name) === EXCLUDE &&
        row.exclusion ? (
          <>
            <Typography variant="body2" noWrap>
              {!row.exclusion.data.attributes.note &&
                row.exclusion.data.attributes.standardMessage}
            </Typography>
            <Typography variant="body2" noWrap>
              {row.exclusion.data.attributes.note &&
                row.exclusion.data.attributes.note}
            </Typography>
          </>
        ) : (
          <></>
        )}
      </Typography>
      <Typography variant="body2" noWrap>
        {row.enrolledBy ? (
          <>{`${t("employees.ilt.table.columns.comments.enrolledBy")}: ${
            row.enrolledBy.data.attributes.fullName
          }`}</>
        ) : (
          <></>
        )}
      </Typography>
    </>
  );
};

const ActionsColumn = ({ row, t, openModal }) => {
  return (
    <Box display="flex" height="0px" alignItems="center">
      {row && !!row.enrolledTrainingObject && (
        <Button variant="text" color="primary" onClick={e => openModal(e, row)}>
          {t("userAssignments.viewDetails")}
        </Button>
      )}
    </Box>
  );
};

const columns = (t, openModal) => [
  {
    Header: t("table.columnHeader.Type"),
    accessor: row => (
      <Box display="flex" justifyContent="center">
        <CourseTypeIcon type={row.trainingObject.type.name} />
      </Box>
    ),
    id: "trainingObjectTypes.name",
    style: {
      width: 62,
      maxWidth: 62
    }
  },
  {
    Header: t("table.columnHeader.Title"),
    accessor: "trainingObject.name",
    canSort: true,
    Filter: TextFilter,
    filterPlaceholder: t("userAssignments.titleAll"),
    id: "trainingObjects.name"
  },
  {
    Header: t("table.columnHeader.DueDate"),
    accessor: row => <DueDateDot row={row} dueDate={row.dueDate} />,
    Filter: TextFilter,
    filter: (rows, columnIds, filterValue) => {
      return rows.filter(row => {
        const { label } = decideDateLabel(
          row.original.dueDate,
          !!row.original.exclusion
        );
        return label.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1;
      });
    },
    canSort: true,
    filterPlaceholder: t("table.columnHeader.DueDate"),
    id: "dueDate"
  },
  {
    Header: t("table.columnHeader.Comments"),
    accessor: row => <Comments row={row} t={t} />,
    Filter: TextFilter,
    filter: (rows, columnIds, filterValue) => {
      return rows.filter(row => {
        let comments = `${row.original.assignmentType.description} ${
          !!row.original.exclusion
            ? row.original.exclusion.data.attributes.note
            : ""
        } ${`${t("employees.ilt.table.columns.comments.assignedBy")}: ${
          row.original.assignedBy
            ? row.original.assignedBy.fullName
            : t("employees.ilt.table.columns.comments.assignedByNull")
        }`} ${
          row.original.enrolledBy
            ? `${t("employees.ilt.table.columns.comments.enrolledBy")}:${
                row.original.enrolledBy.data.attributes.fullName
              }`
            : ""
        }`;
        return comments.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1;
      });
    },
    canSort: true,
    filterPlaceholder: t("userAssignments.commentsAll"),
    id: "comments"
  },
  {
    Header: t("table.columnHeader.actions"),
    accessor: row => <ActionsColumn row={row} t={t} openModal={openModal} />,
    canSort: false,
    disableSortBy: true
  }
];

const ClarifModal = ({ onResult, employeeName, specialAssignments }) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={true}
      showCloseIcon
      maxWidth="sm"
      fullWidth
      dialogContent={
        <Typography variant="body1">
          {t("userAssignments.confirmUpdate", {
            employeeName,
            specialAssignments
          })}
        </Typography>
      }
      dialogActions={
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onResult(true)}
          >
            {t("userAssignments.yes")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onResult(false)}
          >
            {t("userAssignments.no")}
          </Button>
        </>
      }
      onClose={() => onResult(false)}
      data-qa="confirm-edit-modal"
    />
  );
};

const Assignments = ({ employeeID }) => {
  const { expanded } = useStyles();
  const [selection, setSelection] = useState([]);
  const [sortBy, setSortBy] = useState([]);
  const [updatedDates, setUpdatedDates] = useState({});
  const [updatedRows, setUpdatedRows] = useState([]);
  const [deletedRows, setDeletedRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [createAssignmentModal, setCreateAssignmentModal] = useState(false);
  const { userAssignments, isLoading } = useUserAssignments(
    employeeID,
    sortBy,
    updatedRows,
    deletedRows
  );
  const print = usePrintAssignments(employeeID);
  const email = useEmailAssignments(employeeID);
  const {
    isAdmin,
    notSaveEmployeeAssignments,
    canEditEmployeeAssignments,
    isGoldOrPlatinum
  } = usePermissions();
  const { getUserAssignment, loading } = useUserAssignment();
  const [currentProgress, setCurrentProgress] = useState(null);
  const [completedActivities, setCompletedActivities] = useState(0);
  const [totalCompletion, setTotalCompletion] = useState(0);
  const [rowCourseName, setRowCourseName] = useState(null);
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const classes = useStyles();

  const openClarifModal = useModal(ClarifModal);

  const openModal = (e, row) => {
    setRowCourseName(row.trainingObject.name);
    getUserAssignment(row.id, employeeID).then(data => {
      const completed = data.filter(
        p => p.status === assignmentStatuses.completed
      ).length;
      setCurrentProgress(data);
      setCompletedActivities(completed);
      setTotalCompletion(Math.round((completed / data.length) * 100));
    });
    setViewDetailsModalOpen(true);
    e.stopPropagation();
  };

  const { assignmentExcluded, setAssignmentExcluded } = useDeleteAndExclude;

  const presentDeleteCallback = removed => {
    setDeletedRows(deletedRows => [...deletedRows, ...removed]);
  };
  const presentUpdateCallback = updated => {
    setUpdatedRows([...updatedRows, ...updated]);
  };

  const batchUpdate = useBulkUserAssignmentSave(
    employeeID,
    presentUpdateCallback
  );
  const { label, call } = useDeleteAndExclude(
    employeeID,
    selection,
    presentDeleteCallback,
    presentUpdateCallback
  );

  const { t } = useTranslation();
  const memoColumns = useMemo(() => columns(t, openModal), []);

  const stateChange = useCallback(
    state => {
      if (state.sortBy && !isEqual(state.sortBy, sortBy))
        setSortBy(state.sortBy);
    },
    [sortBy]
  );

  const updateSelection = useCallback((rowIds, rawSelection) => {
    const newSelection = Object.entries(rawSelection).map(row => {
      return {
        ...row[1].original,
        toggleRowSelected: row[1].toggleRowSelected
      };
    });

    const getId = row => row.id;

    setSelection(selection =>
      !isEqual(newSelection.map(getId), selection.map(getId))
        ? newSelection
        : selection
    );
  }, []);

  const saveDueDate = async (e, { setSubmitting }) => {
    const userAssignment = userAssignments.find(
      assignment => assignment.id === e["id"]
    );
    if (userAssignment.assignmentType.name === "Special") {
      const clarificationResult = await openClarifModal({
        employeeName: userAssignment.user.fullName,
        specialAssignments: userAssignment.assignment.name
      }).result;
      if (clarificationResult) updateAssignment(e, setSubmitting);
    } else {
      updateAssignment(e, setSubmitting);
    }
  };

  const updateAssignment = (e, setSubmitting) => {
    e["assignmentType"] = { name: "Individual" };
    e["dueDate"] = moment(e["dueDate"]).format("YYYY-MM-DD");
    batchUpdate(formatBatchUpdate([e]));
    setSubmitting(false);
  };

  const updateDueDates = async () => {
    const specialAssignmentsNames = [];
    const selectedAssignments = selection.filter(row => updatedDates[row.id]);
    selectedAssignments.forEach(row => {
      if (row.assignmentType.name === "Special")
        specialAssignmentsNames.push(row.assignment.name);
    });
    if (specialAssignmentsNames.length > 0) {
      const clarificationResult = await openClarifModal({
        employeeName: selectedAssignments[0].user.fullName,
        specialAssignments: specialAssignmentsNames.join(", ")
      }).result;
      if (clarificationResult) {
        updateAssignments(selectedAssignments);
      }
    } else {
      updateAssignments(selectedAssignments);
    }
  };

  const updateAssignments = selectedAssignments => {
    batchUpdate(
      formatBatchUpdate(
        selectedAssignments.map(row => ({
          id: row.id,
          dueDate: updatedDates[row.id],
          assignmentType: { name: "Individual" }
        }))
      )
    );
  };

  const allowUpdating = rowData => {
    return (
      !notSaveEmployeeAssignments &&
      !rowData.exclusion &&
      (isAdmin ||
        (rowData.assignedBy &&
          !(rowData.assignedBy.isAdmin || rowData.assignedBy.visionAssignment)))
    );
  };

  const updateExclusion = useUpdateExclusion(employeeID);

  return (
    <>
      <Box mb={3} justifyContent="space-between" display="flex" flexWrap="wrap">
        <ButtonGroup align="left">
          {!notSaveEmployeeAssignments && canEditEmployeeAssignments && (
            <>
              <TooltipButton
                onClick={() => setCreateAssignmentModal(true)}
                variant="contained"
                color="primary"
                startIcon={<Add />}
                className={classes.addAssignmentButton}
                tooltipProps={{
                  title: isGoldOrPlatinum
                    ? ""
                    : t("administration.goldPlatinumFeatureTooltip")
                }}
                disabled={!isGoldOrPlatinum}
              >
                {t("userAssignments.addUserAssignment")}
              </TooltipButton>
              <Button
                variant="contained"
                color="primary"
                onClick={updateDueDates}
              >
                {t("userAssignments.update")}
              </Button>
              {label && !assignmentExcluded ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={
                    label === CANCEL
                      ? () => {
                          call();
                          setAssignmentExcluded(!assignmentExcluded);
                        }
                      : () => setOpen(true)
                  }
                >
                  {label}
                </Button>
              ) : (
                <></>
              )}
            </>
          )}
        </ButtonGroup>
        <ButtonGroup align="left" flexWrap="wrap">
          {print && (
            <Button color="primary" startIcon={<Print />} onClick={print}>
              {t("userAssignments.printAssignment")}
            </Button>
          )}
          {email && (
            <Button color="primary" startIcon={<Email />} onClick={email}>
              {t("userAssignments.emailAssignment")}
            </Button>
          )}
        </ButtonGroup>
      </Box>
      <Table
        loading={isLoading}
        data={userAssignments}
        columns={memoColumns}
        onStateChange={stateChange}
        updateSelection={updateSelection}
        showSelectAllCheckbox={
          notSaveEmployeeAssignments
            ? false
            : userAssignments.filter(assignment => !assignment.exclusion)
                .length !== 0
            ? true
            : false
        }
        expandedClass={expanded}
        showCheckbox={row => allowUpdating(row) && canEditEmployeeAssignments}
        disableMainCheckBox={true}
        manualFilters={false}
        detailPanel={row =>
          !row.exclusion ? (
            <AssignmentDetail
              rowData={row}
              onSubmit={saveDueDate}
              setDates={setUpdatedDates}
              presentDeleteCallback={presentDeleteCallback}
              presentUpdateCallback={presentUpdateCallback}
              disableUpdating={
                !allowUpdating(row) || !canEditEmployeeAssignments
              }
            />
          ) : (
            <ExclusionDetail
              rowData={row}
              onSubmit={updateExclusion}
              setDates={setUpdatedDates}
              presentDeleteCallback={presentDeleteCallback}
              presentUpdateCallback={presentUpdateCallback}
            />
          )
        }
      />
      <CreateAssignmentsModal
        onClose={() => setCreateAssignmentModal(false)}
        open={createAssignmentModal}
        employeeID={employeeID}
        presentUpdateCallback={presentUpdateCallback}
      />
      <CourseExclusionSettingsModal
        onClose={() => setOpen(false)}
        open={open}
        onSubmit={exclusionData => {
          call(exclusionData);
          setSelection([]);
          setOpen(false);
        }}
        title={t(`userAssignments.${label}`)}
      />
      <CourseProgressModal
        setModalOpen={setViewDetailsModalOpen}
        open={viewDetailsModalOpen}
        trainingName={rowCourseName}
        loading={loading}
        currentProgress={currentProgress}
        completedActivities={completedActivities}
        totalCompletion={totalCompletion}
      />
    </>
  );
};

export default Assignments;
